import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {ReplyData, ReplyEvent} from '../../_types/shared';
import {User} from '../../_types/user';
import {AuthService} from '../../_services/auth.service';
import {FormControl} from '@angular/forms';
import {Post, PostAttachment, PostAttachmentType, PostComment, PostNewComment} from '../../_types/content';
import {switchMap, take} from 'rxjs';
import {SharedStateService} from '../../_services/shared-state.service';
import {ContentService} from '../../_services/content.service';
import {MatSnackBar} from "@angular/material/snack-bar";
import {Community, UserAccessLevel} from "../../_types/communities";
import {LocalStorageService} from "../../_services/local-storage.service";
import {SnackbarService} from '../../_services/snackbar.service';

@Component({
    selector: 'app-reply-field',
    templateUrl: './reply-field.component.html',
    styleUrl: './reply-field.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReplyFieldComponent implements OnInit, OnChanges {
    @Input() isReply: boolean = false;
    @Input() isOpenState: boolean = false;
    @ViewChild('replyField') replyField: any;
    @Input({required: true}) post!: Post;
    @Output() action: EventEmitter<ReplyEvent<ReplyData>> = new EventEmitter<ReplyEvent<ReplyData>>();
    comment: FormControl<string | null> = new FormControl('');
    attachmentsValid: boolean = true;
    attachment: any;
    attachmentType: string = 'text';
    attachmentInitial: boolean = false;
    is_anonymous: boolean = false;
    placeholder: string = '';
    selectedCommunity: Community | null = null;
    currentUser: User | null = null;
    isMobile: boolean = window.innerWidth <= 1000;
    private readonly authService: AuthService = inject(AuthService);
    private readonly sharedStateService: SharedStateService = inject(SharedStateService);
    private readonly contentService: ContentService = inject(ContentService);
    private readonly snackBar: MatSnackBar = inject(MatSnackBar);
    private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
    private readonly snackbarService: SnackbarService = inject(SnackbarService);

    private _initialCommentData?: PostComment;

    get initialCommentData(): PostComment | undefined {
        return this._initialCommentData;
    }

    @Input() set initialCommentData(value: PostComment) {
        this._initialCommentData = value;
        this.is_anonymous = !!value?.is_anonymous;
        this.comment.patchValue(value.text);
    };

    get initialCommentDataAttachment(): PostAttachment | null {
        return this._initialCommentData?.attachments?.[0] || null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isReply'] || changes['isOpenState']) {
            this.placeholder = this.isReply ? 'Write a reply...' : !this.isOpenState ? 'Add a comment' : 'Write a comment...'
        }
    }

    ngOnInit(): void {
        this.selectedCommunity = this.localStorageService.getItem<Community | null>(
            'lastSelectedCommunity',
            null
        );
        this.currentUser = this.authService.getUser();
    }

    commentFocus(replyField: HTMLTextAreaElement): void {
        if (this.isMobile) {
            setTimeout((): void => {
                replyField.scrollIntoView(true)
            }, 500);
        }
        this.setState(true)
    }

    setState(state: boolean): void {
        this.isOpenState = state;
    }

    attachmentsValidEmitter(event: boolean): void {
        this.attachmentsValid = event
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.contentService.userHasCommunityRole(roles);
    }

    getAttachmentField(event: any): void {
        this.attachmentType = event.type;
        this.attachment = event.value;
        this.attachmentInitial = event.initial;
        if (this.attachmentType == 'text') {
            this.attachmentsValid = true;
        }
    }

    reset(): void {
        if (this.isReply) this.sharedStateService.removeVariable('replyCommentId');
        this.comment.reset('');
        this.setState(false);
        this.action.emit({action: 'cancel', data: {comment: this.initialCommentData!} as any});
    }

    submitComment(): void {
        let commentData: PostNewComment;
        let type: PostAttachmentType | 'text';

        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';
                this.attachment = this.attachment.embed_url;
                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }
        commentData = this.addAttachmentToPayload(type) as PostNewComment;


        const replyId = this.isReply && this.sharedStateService.getCurrentValue('replyCommentId');
        if (replyId) {
            commentData = {
                ...commentData,
                parent: replyId
            };
        }
        if (this.is_anonymous) {
            commentData = {
                ...commentData,
                is_anonymous: this.is_anonymous
            };
        }

        this.contentService.newPostComments(this.post.id, type, commentData)
            .pipe(
                switchMap(() => this.contentService.getPostComments(this.post.id)),
                take(1)
            )
            .subscribe(res => {
                this.post.comments_count = res.length;
                this.sharedStateService.updateModalComments(res);
                this.replyField?.nativeElement.focus()
            }, error => {
                this.openSnackBar(error.message || 'An error occurred please try again later', 'error')
            });
        if (this.isReply) this.sharedStateService.removeVariable('replyCommentId');
        this.comment.reset('');
        this.setState(false);
    }

    patchComment(): void {
        let commentData: PostComment;
        let type: PostAttachmentType | 'text';

        switch (this.attachmentType) {
            case 'videoLink':
                type = 'video_url';
                break;
            case 'gif':
                type = 'gif_url';
                this.attachment = this.attachment.embed_url;
                break;
            default:
                type = this.attachmentType as PostAttachmentType | 'text';
        }
        commentData =
            commentData = {
                ...this.addAttachmentToPayload(type) as PostComment,
                moderated: false
            };


        const replyId = this.isReply && this.sharedStateService.getCurrentValue('replyCommentId');
        if (replyId) {
            commentData = {
                ...commentData,
                parent: replyId,
            };
        }

        commentData = {
            ...commentData,
            is_anonymous: this.is_anonymous,
        };

        if (this.initialCommentDataAttachment) {
            commentData = {
                ...commentData,
                attachments_to_delete: this.initialCommentData?.attachments.map(a => a.id).join(", ")
            } as any
        }

        this.contentService.patchComment(this.post.id, this.initialCommentData?.id!, commentData, type).subscribe(r => {
            this.action.emit({action: 'submit', data: {comment: r.data} as any});
            this.snackbarService.openSuccessSnackBar('Comment has been updated');
            this.reset();
        }, e => {
            this.snackbarService.openErrorSnackBar(e);
        })
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    validate(formControl: FormControl): void {
        formControl.markAllAsTouched();
    }

    private addAttachmentToPayload(type: PostAttachmentType | 'text'): PostNewComment {
        if (type === 'video_url') {
            return {
                text: this.comment.value!,
                video_urls: this.attachment
            };
        } else if (type === 'gif_url') {
            return {
                text: this.comment.value!,
                gif_urls: this.attachment
            };
        } else if (type == 'url') {
            return {
                text: this.comment.value!,
                url: this.attachment
            };
        } else if (type !== 'text') {
            return {
                text: this.comment.value!,
                attachment_files: this.attachment
            };
        } else {
            return {
                text: this.comment.value!,
            }
        }
    }

}
